import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { httpClient } from '@/shared/api/httpClient';
import { endpoints } from '@/shared/api/endpoints';
import { queryKeys } from '@/shared/lib/query/queryKeys';
import { getErrorMessage } from '@/shared/api/errors';
import { getAccessToken } from '@/shared/lib/storage/token';
import { toAuthUserPatch } from '@/shared/lib/user/toAuthUserPatch';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import { updateUser } from '@/shared/store/slices/authSlice';

export type ProfileData = {
  _id?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  mobileNumber?: string;
  mobileCountryCode?: string;
  phone?: string;
  companyName?: string;
  role?: string;
  accountType?: string;
  status?: string;
  profilePic?: { url?: string; id?: string; _id?: string; mediaId?: string } | string | null;
  address?: {
    fullAddress?: string;
    locality?: string;
    street?: string;
    pincode?: string;
    city?: string;
    state?: string;
    country?: string;
  };
  country?: string;
  dynamicFields?: Record<string, unknown>;
  aadhaarVerification?: {
    status?: string;
    verifiedAt?: string;
  };
  [key: string]: unknown;
};

export function useProfileQuery(options?: { enabled?: boolean }) {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const hasSession = Boolean(getAccessToken()) || isAuthenticated;

  const query = useQuery({
    queryKey: [...queryKeys.auth.root, 'profile'],
    queryFn: async () => {
      const { data } = await httpClient.get(endpoints.profile.view);
      return data as { success?: boolean; data?: ProfileData };
    },
    // Never hit profile APIs while logged out; callers may only narrow further.
    enabled: hasSession && (options?.enabled ?? true),
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    const profile = query.data?.data;
    if (!profile) return;
    const patch = toAuthUserPatch(profile);
    if (Object.keys(patch).length) dispatch(updateUser(patch));
  }, [dispatch, query.data]);

  return query;
}

export function useUpdateProfileMutation() {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  return useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const { data } = await httpClient.patch(endpoints.profile.update, payload);
      return data as { success?: boolean; message?: string; data?: ProfileData };
    },
    onSuccess: (res, payload) => {
      toast.success(res?.message || 'Profile updated');
      const patch = toAuthUserPatch(
        (res?.data as Record<string, unknown> | undefined) || payload,
      );
      if (Object.keys(patch).length) dispatch(updateUser(patch));
      void queryClient.invalidateQueries({ queryKey: queryKeys.auth.root });
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Update failed')),
  });
}

export function useChangePasswordMutation() {
  return useMutation({
    mutationFn: async (payload: {
      currentPassword: string;
      newPassword: string;
      confirmPassword: string;
    }) => {
      const { data } = await httpClient.post(
        endpoints.profile.changePassword,
        payload,
      );
      return data as { success?: boolean; message?: string };
    },
    onSuccess: (res) => toast.success(res?.message || 'Password changed'),
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to change password')),
  });
}
